import {
  AlertTriangle,
  ArrowUpRight,
  Building2,
  CheckCircle2,
  CircleDollarSign,
  Download,
  Gauge,
  Info,
  MapPin,
  RotateCcw,
  Route,
  SlidersHorizontal,
  Sparkles,
  Target
} from "lucide-react";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import type {
  BusinessAnalysisAoi,
  BusinessAnalysisRadiusKey,
  BusinessAnalysisScore,
  BusinessAnalysisSnapshot
} from "../../shared/types";
import { StateBlock } from "../components/common/StateBlock";
import {
  COMMERCIAL_WEIGHT_KEYS,
  DEFAULT_WEIGHT_SCENARIO,
  DEMOGRAPHIC_WEIGHT_KEYS,
  TOTAL_OPPORTUNITY_WEIGHT,
  isDefaultWeightScenario,
  recalculateBusinessAnalysis,
  redistributeGroupWeight,
  scaleWeightGroup,
  weightGroupTotal,
  type BusinessAnalysisWeightScenario,
  type CommercialWeightKey,
  type DemographicWeightKey,
  type ScenarioWeightKey
} from "../lib/businessAnalysisScenario";
import { downloadBusinessAnalysisPdf } from "../lib/businessAnalysisPdf";
import { getBusinessAnalysis } from "../services/businessAnalysisService";
import "../styles/businessAnalysis.css";

const compactNumber = new Intl.NumberFormat("en-CA", { notation: "compact", maximumFractionDigits: 1 });
const currency = new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", notation: "compact", maximumFractionDigits: 1 });
const integer = new Intl.NumberFormat("en-CA", { maximumFractionDigits: 0 });
const radarGroups: Array<{ metric: string; keys: BusinessAnalysisScore["key"][] }> = [
  { metric: "Residential market", keys: ["residentialMarket"] },
  { metric: "Convenience", keys: ["food", "snacksBeverage"] },
  { metric: "Tobacco", keys: ["tobacco"] },
  { metric: "Cigarettes", keys: ["cigarettes"] },
  { metric: "Cannabis", keys: ["cannabis"] },
  { metric: "Fuel", keys: ["fuel"] }
];
const weightStorageKey = "business-analysis-weight-scenario-v2";
const commercialWeightControls: Array<{ key: CommercialWeightKey; label: string }> = [
  { key: "fuel", label: "Fuel" },
  { key: "food", label: "Food" },
  { key: "snacksBeverage", label: "Snacks & Beverage" },
  { key: "tobacco", label: "Vapes / Tobacco" },
  { key: "cigarettes", label: "Cigarettes" },
  { key: "cannabis", label: "Cannabis" }
];
const demographicWeightControls: Array<{ key: DemographicWeightKey; label: string }> = [
  { key: "growth", label: "Growth" },
  { key: "residentialMarket", label: "Residential market size" },
  { key: "daytimeMarket", label: "Daytime market size" },
  { key: "ageProfile", label: "Age profile fit" }
];

function storedWeightScenario(): BusinessAnalysisWeightScenario {
  if (typeof window === "undefined") return { ...DEFAULT_WEIGHT_SCENARIO };
  try {
    const parsed = JSON.parse(window.localStorage.getItem(weightStorageKey) || "{}") as Partial<BusinessAnalysisWeightScenario>;
    const keys = Object.keys(DEFAULT_WEIGHT_SCENARIO) as ScenarioWeightKey[];
    const scenario = Object.fromEntries(keys.map((key) => [key, Number(parsed[key])])) as BusinessAnalysisWeightScenario;
    const valid = keys.every((key) => Number.isFinite(scenario[key]) && scenario[key] >= 0 && scenario[key] <= TOTAL_OPPORTUNITY_WEIGHT);
    const total = weightGroupTotal(scenario, keys);
    return valid && total === TOTAL_OPPORTUNITY_WEIGHT ? scenario : { ...DEFAULT_WEIGHT_SCENARIO };
  } catch {
    return { ...DEFAULT_WEIGHT_SCENARIO };
  }
}

function formatMetric(score: BusinessAnalysisScore, benchmark = false): string {
  const value = benchmark ? score.benchmarkValue : score.value;
  if (score.unit === "currency") return currency.format(value);
  if (score.unit === "percent") return `${(value * 100).toFixed(1)}%`;
  return integer.format(value);
}

function scoreTone(score: number): string {
  if (score >= 80) return "excellent";
  if (score >= 65) return "strong";
  if (score >= 50) return "moderate";
  return "caution";
}

function formatScore(score: number): string {
  return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

function recommendationCopy(aoi: BusinessAnalysisAoi): string {
  switch (aoi.recommendation) {
    case "Prioritize": return "High-priority candidate. Proceed to site economics and field validation.";
    case "Advance with validation": return "Promising candidate. Validate traffic, competition, land cost, and access.";
    case "Monitor": return "Mixed market signal. Retain in the pipeline and reassess with additional evidence.";
    default: return "Current Esri profile does not support prioritizing development.";
  }
}

function ScoreRing({ score }: { score: number }) {
  return (
    <div className={`business-score-ring business-score-ring--${scoreTone(score)}`} style={{ "--score": `${score * 3.6}deg` } as CSSProperties}>
      <div><strong>{formatScore(score)}</strong><span>/ 100</span></div>
    </div>
  );
}

export function BusinessAnalysisPage() {
  const [snapshot, setSnapshot] = useState<BusinessAnalysisSnapshot>();
  const [selectedAoiSk, setSelectedAoiSk] = useState<string>();
  const [selectedRadius, setSelectedRadius] = useState<BusinessAnalysisRadiusKey>("R20");
  const [weightScenario, setWeightScenario] = useState<BusinessAnalysisWeightScenario>(storedWeightScenario);
  const [draftWeights, setDraftWeights] = useState<BusinessAnalysisWeightScenario>(() => ({ ...weightScenario }));
  const [weightEditorOpen, setWeightEditorOpen] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportError, setExportError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  async function load() {
    setLoading(true);
    setError(undefined);
    try {
      const next = await getBusinessAnalysis();
      setSnapshot(next);
      setSelectedAoiSk((current) => current && next.aois.some((item) => item.aoiSk === current) ? current : next.aois[0]?.aoiSk);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load Business Analysis.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  const scenarioSnapshot = useMemo(
    () => snapshot ? recalculateBusinessAnalysis(snapshot, weightScenario) : undefined,
    [snapshot, weightScenario]
  );
  const customScenario = !isDefaultWeightScenario(weightScenario);
  const activeCommercialTotal = weightGroupTotal(weightScenario, COMMERCIAL_WEIGHT_KEYS);
  const activeDemographicTotal = weightGroupTotal(weightScenario, DEMOGRAPHIC_WEIGHT_KEYS);
  const draftCommercialTotal = weightGroupTotal(draftWeights, COMMERCIAL_WEIGHT_KEYS);
  const draftDemographicTotal = weightGroupTotal(draftWeights, DEMOGRAPHIC_WEIGHT_KEYS);
  const selectedAoi = useMemo(() => scenarioSnapshot?.aois.find((item) => item.aoiSk === selectedAoiSk) || scenarioSnapshot?.aois[0], [selectedAoiSk, scenarioSnapshot]);
  const defaultSelectedAoi = snapshot?.aois.find((item) => item.aoiSk === selectedAoi?.aoiSk);
  const selectedResult = selectedAoi?.results.find((item) => item.radiusKey === selectedRadius) || selectedAoi?.results[0];
  const radarData = selectedResult ? radarGroups.map(({ metric, keys }) => {
    const groupScores = selectedResult.scores.filter((item) => keys.includes(item.key));
    const divisor = groupScores.length || 1;
    return {
      metric,
      AOI: Math.round(groupScores.reduce((total, item) => total + item.score, 0) / divisor),
      "Comparable station": Math.round(groupScores.reduce((total, item) => total + item.benchmarkScore, 0) / divisor)
    };
  }) : [];
  const categoryData = selectedResult?.categories.map((item) => ({
    category: item.category === "Snacks & Beverage" ? "Snacks" : item.category,
    AOI: Math.round(item.aoiExpenditure), Benchmark: Math.round(item.benchmarkExpenditure), index: item.index
  })) || [];
  const comparableSales = scenarioSnapshot?.salesBenchmarks.filter((row) =>
    row.stationSk === selectedResult?.comparableStation.poiSk || row.stationName === selectedResult?.comparableStation.name
  ) || [];

  function updateMarketMix(commercialWeight: number) {
    const commercialTotal = Math.max(0, Math.min(TOTAL_OPPORTUNITY_WEIGHT, commercialWeight || 0));
    setDraftWeights((current) => {
      const commercialScaled = scaleWeightGroup(current, COMMERCIAL_WEIGHT_KEYS, commercialTotal);
      return scaleWeightGroup(commercialScaled, DEMOGRAPHIC_WEIGHT_KEYS, TOTAL_OPPORTUNITY_WEIGHT - commercialTotal);
    });
  }

  function updateDraftWeight(keys: ScenarioWeightKey[], key: ScenarioWeightKey, value: number) {
    setDraftWeights((current) => redistributeGroupWeight(current, keys, key, value));
  }

  function applyWeightScenario() {
    const next = { ...draftWeights };
    setWeightScenario(next);
    window.localStorage.setItem(weightStorageKey, JSON.stringify(next));
    setWeightEditorOpen(false);
  }

  function resetWeightScenario() {
    const defaults = { ...DEFAULT_WEIGHT_SCENARIO };
    setDraftWeights(defaults);
    setWeightScenario(defaults);
    window.localStorage.removeItem(weightStorageKey);
  }

  async function exportAoiReport() {
    if (!snapshot || !selectedAoi) return;
    setExportingPdf(true);
    setExportError(undefined);
    try {
      await downloadBusinessAnalysisPdf({
        snapshot,
        aoi: selectedAoi,
        defaultAoi: defaultSelectedAoi,
        detailRadius: selectedRadius,
        weights: weightScenario,
        customScenario
      });
    } catch (exportFailure) {
      setExportError(exportFailure instanceof Error ? exportFailure.message : "Unable to export the AOI report.");
    } finally {
      setExportingPdf(false);
    }
  }

  if (loading) return <StateBlock type="loading" title="Building site suitability model" message="Comparing Esri trade areas with operating station benchmarks…" />;
  if (error || !snapshot || !scenarioSnapshot || !selectedAoi || !selectedResult) {
    return <StateBlock type="error" title="Business Analysis unavailable" message={error || "No AOI records were returned."} />;
  }

  return (
    <section className="page-stack business-analysis-page">
      <div className="page-heading business-analysis-heading">
        <div>
          <p>Location intelligence · Esri trade areas</p>
          <h2>Business Analysis</h2>
          <span>Evaluate development candidates against the profile and realized performance of Gen7 stations.</span>
        </div>
        <div className="business-analysis-actions">
          <button
            className={`business-weight-toggle${customScenario ? " active" : ""}`}
            onClick={() => {
              setDraftWeights({ ...weightScenario });
              setWeightEditorOpen((open) => !open);
            }}
            type="button"
          >
            <SlidersHorizontal size={16} />
            {customScenario ? "Custom weights" : "Adjust weights"}
          </button>
          <label>
            <span>Area of interest</span>
            <select value={selectedAoi.aoiSk} onChange={(event) => setSelectedAoiSk(event.target.value)}>
              {scenarioSnapshot.aois.map((aoi) => <option key={aoi.aoiSk} value={aoi.aoiSk}>{aoi.name}</option>)}
            </select>
          </label>
          <button className="business-export-button" disabled={exportingPdf} onClick={() => void exportAoiReport()} type="button">
            <Download size={16} />
            {exportingPdf ? "Building PDF..." : "Export AOI report"}
          </button>
          {exportError && <span aria-live="polite" className="business-export-error">{exportError}</span>}
        </div>
      </div>

      {weightEditorOpen && (
        <section className="panel business-weight-panel">
          <div className="business-weight-panel__heading">
            <div>
              <p>Weight scenario builder</p>
              <h3>Adjust the evaluation priorities</h3>
              <span>Set the market mix first, then refine the weights within each group. Group totals are balanced automatically.</span>
            </div>
            <div className="business-weight-total valid">
              <strong>{draftCommercialTotal + draftDemographicTotal}%</strong><span>/ {TOTAL_OPPORTUNITY_WEIGHT}%</span>
            </div>
          </div>

          <div className="business-weight-stage">
            <div className="business-weight-stage__heading"><i>1</i><div><strong>Expenditure vs demographic mix</strong><span>Choose how much of the Opportunity Score comes from commercial potential and market context.</span></div></div>
            <div className="business-weight-mix">
              <label>
                <span>Esri category expenditure</span>
                <input aria-label="Expenditure weight" max={TOTAL_OPPORTUNITY_WEIGHT} min="0" onChange={(event) => updateMarketMix(Number(event.target.value))} step="1" type="range" value={draftCommercialTotal} />
                <div><input aria-label="Expenditure weight percentage" max={TOTAL_OPPORTUNITY_WEIGHT} min="0" onChange={(event) => updateMarketMix(Number(event.target.value))} step="1" type="number" value={draftCommercialTotal} /><span>%</span></div>
              </label>
              <label>
                <span>Demographic market context</span>
                <input aria-label="Demographic weight" max={TOTAL_OPPORTUNITY_WEIGHT} min="0" onChange={(event) => updateMarketMix(TOTAL_OPPORTUNITY_WEIGHT - Number(event.target.value))} step="1" type="range" value={draftDemographicTotal} />
                <div><input aria-label="Demographic weight percentage" max={TOTAL_OPPORTUNITY_WEIGHT} min="0" onChange={(event) => updateMarketMix(TOTAL_OPPORTUNITY_WEIGHT - Number(event.target.value))} step="1" type="number" value={draftDemographicTotal} /><span>%</span></div>
              </label>
            </div>
          </div>

          <div className="business-weight-stage">
            <div className="business-weight-stage__heading"><i>2</i><div><strong>Commercial weight scenario</strong><span>Reallocate the {draftCommercialTotal}% commercial share. Changing one category automatically balances the others.</span></div></div>
            <div className="business-weight-controls">
              {commercialWeightControls.map(({ key, label }) => (
                <label className="business-weight-control" key={key}>
                  <span>{label}</span>
                  <input aria-label={`${label} weight`} max={draftCommercialTotal} min="0" onChange={(event) => updateDraftWeight(COMMERCIAL_WEIGHT_KEYS, key, Number(event.target.value))} step="0.5" type="range" value={draftWeights[key]} />
                  <div><input aria-label={`${label} weight percentage`} max={draftCommercialTotal} min="0" onChange={(event) => updateDraftWeight(COMMERCIAL_WEIGHT_KEYS, key, Number(event.target.value))} step="0.5" type="number" value={draftWeights[key]} /><span>%</span></div>
                </label>
              ))}
            </div>
          </div>

          <div className="business-weight-stage">
            <div className="business-weight-stage__heading"><i>3</i><div><strong>Demographic weights scenario</strong><span>Reallocate the {draftDemographicTotal}% demographic share across the four market-context measures.</span></div></div>
            <div className="business-weight-controls">
              {demographicWeightControls.map(({ key, label }) => (
                <label className="business-weight-control" key={key}>
                  <span>{label}</span>
                  <input aria-label={`${label} weight`} max={draftDemographicTotal} min="0" onChange={(event) => updateDraftWeight(DEMOGRAPHIC_WEIGHT_KEYS, key, Number(event.target.value))} step="0.5" type="range" value={draftWeights[key]} />
                  <div><input aria-label={`${label} weight percentage`} max={draftDemographicTotal} min="0" onChange={(event) => updateDraftWeight(DEMOGRAPHIC_WEIGHT_KEYS, key, Number(event.target.value))} step="0.5" type="number" value={draftWeights[key]} /><span>%</span></div>
                </label>
              ))}
            </div>
          </div>

          <div className="business-weight-panel__footer">
            <p>Apply the scenario to recalculate every AOI and radius. The approved BA-2.0 result remains available for comparison.</p>
            <div>
              <button className="business-weight-reset" onClick={resetWeightScenario} type="button"><RotateCcw size={15} /> Reset defaults</button>
              <button className="business-weight-apply" onClick={applyWeightScenario} type="button">Apply scenario</button>
            </div>
          </div>
        </section>
      )}

      <div className="business-analysis-summary-grid">
        <section className="panel business-decision-card">
          <div className="business-decision-card__top">
            <div>
              <span className="business-eyebrow"><MapPin size={14} /> AOI {selectedAoi.aoiSk}</span>
              <h3>{selectedAoi.name}</h3>
              <p>{[selectedAoi.city, selectedAoi.province].filter(Boolean).join(", ") || selectedAoi.address || "Candidate trade area"}</p>
            </div>
            <ScoreRing score={selectedAoi.overallScore} />
          </div>
          <div className={`business-recommendation business-recommendation--${scoreTone(selectedAoi.overallScore)}`}>
            <Sparkles size={17} />
            <div><strong>{selectedAoi.recommendation}</strong><span>{recommendationCopy(selectedAoi)}</span></div>
          </div>
          {customScenario && defaultSelectedAoi && (
            <div className="business-scenario-comparison">
              <span>BA-2.0 default <strong>{formatScore(defaultSelectedAoi.overallScore)}</strong></span>
              <span>Custom scenario <strong>{formatScore(selectedAoi.overallScore)}</strong></span>
              <b>{selectedAoi.overallScore - defaultSelectedAoi.overallScore >= 0 ? "+" : ""}{formatScore(selectedAoi.overallScore - defaultSelectedAoi.overallScore)}</b>
            </div>
          )}
        </section>

        <section className="panel business-kpi-card">
          <span><Gauge size={17} /> {selectedResult.radiusMinutes}-minute suitability</span>
          <strong>{formatScore(selectedResult.suitabilityScore)}</strong>
          <small>{selectedResult.grade} · opportunity {formatScore(selectedResult.opportunityScore)}</small>
        </section>
        <section className="panel business-kpi-card">
          <span><Target size={17} /> Comparable station</span>
          <strong className="business-kpi-card__name">{selectedResult.comparableStation.name}</strong>
          <small>{selectedResult.stationFitScore}% profile similarity</small>
        </section>
        <section className="panel business-kpi-card">
          <span><CircleDollarSign size={17} /> 20-min market potential</span>
          <strong>{currency.format(selectedAoi.results.find((item) => item.radiusKey === "R20")?.categories.reduce((total, item) => total + item.aoiExpenditure, 0) || 0)}</strong>
          <small>Esri annual category expenditure</small>
        </section>
      </div>

      <div className="business-radius-tabs" role="tablist" aria-label="Drive-time radius">
        {selectedAoi.results.map((result) => (
          <button aria-selected={selectedRadius === result.radiusKey} className={selectedRadius === result.radiusKey ? "active" : ""} key={result.radiusKey} onClick={() => setSelectedRadius(result.radiusKey)} role="tab" type="button">
            <Route size={16} />
            <span>{result.radiusMinutes} min</span>
            <strong>{formatScore(result.suitabilityScore)}</strong>
            <small>{result.grade}</small>
          </button>
        ))}
      </div>

      <div className="business-analysis-grid business-analysis-grid--hero">
        <section className="panel business-chart-panel">
          <div className="panel-heading">
            <div><p>Normalized benchmark comparison</p><h3>AOI opportunity profile</h3></div>
            <span className="business-model-pill">{selectedResult.radiusMinutes}-minute trade area</span>
          </div>
          <div className="business-radar-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} outerRadius="72%">
                <PolarGrid stroke="var(--color-border-strong)" />
                <PolarAngleAxis dataKey="metric" tick={{ fill: "var(--color-text-muted)", fontSize: 12 }} />
                <Radar dataKey="Comparable station" fill="var(--color-cyan)" fillOpacity={0.12} stroke="var(--color-cyan)" strokeWidth={2} />
                <Radar dataKey="AOI" fill="var(--color-accent)" fillOpacity={0.24} stroke="var(--color-accent)" strokeWidth={2} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ background: "var(--color-panel-strong)", border: "1px solid var(--color-border)", borderRadius: 8 }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="panel business-evidence-panel">
          <div className="panel-heading"><div><p>Decision evidence</p><h3>Strengths & watch items</h3></div></div>
          <div className="business-comparable-card">
            <Building2 size={19} />
            <div><span>Most comparable operation</span><strong>{selectedResult.comparableStation.name}</strong><small>{[selectedResult.comparableStation.city, selectedResult.comparableStation.province].filter(Boolean).join(", ")}</small></div>
            <b>{selectedResult.stationFitScore}%</b>
          </div>
          <div className="business-evidence-group">
            <span>Positive signals</span>
            {(selectedResult.highlights.length ? selectedResult.highlights : ["The profile is broadly aligned with the operating-station range."]).map((item) => (
              <div className="business-evidence-item business-evidence-item--positive" key={item}><CheckCircle2 size={16} /><p>{item}</p></div>
            ))}
          </div>
          <div className="business-evidence-group">
            <span>Watch items</span>
            {(selectedResult.risks.length ? selectedResult.risks : ["No material demographic risk surfaced; validate competition, traffic, and parcel economics next."]).map((item) => (
              <div className="business-evidence-item business-evidence-item--risk" key={item}><AlertTriangle size={16} /><p>{item}</p></div>
            ))}
          </div>
        </section>
      </div>

      <section className="panel business-sales-panel">
        <div className="panel-heading"><div><p>CSO actual sales ÷ Esri expenditure</p><h3>Comparable station realization</h3></div><span className="business-model-pill">{selectedResult.comparableStation.name}</span></div>
        {comparableSales.length > 0 ? (
          <div className="business-sales-grid">
            {comparableSales.map((row) => (
              <div key={`${row.stationSk}-${row.category}`}><span>{row.category}</span><strong>{row.realizationRate.toFixed(1)}%</strong><small>{currency.format(row.actualSales)} actual / {currency.format(row.esriExpenditure)} potential</small></div>
            ))}
          </div>
        ) : (
          <div className="business-sales-empty">
            <Info size={17} />
            <div><strong>Actual sales benchmark unavailable</strong><span>No matching CSO sales data was returned for {selectedResult.comparableStation.name}. The Esri market-potential figures below remain available.</span></div>
          </div>
        )}
      </section>

      <section className="panel business-chart-panel">
        <div className="panel-heading">
          <div><p>Esri annual expenditure</p><h3>Category market potential</h3></div>
          <span className="business-model-pill">AOI vs {selectedResult.comparableStation.name}</span>
        </div>
        <div className="business-category-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={categoryData} margin={{ top: 10, right: 18, left: 8, bottom: 0 }}>
              <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="category" tick={{ fill: "var(--color-text-muted)", fontSize: 11 }} />
              <YAxis tick={{ fill: "var(--color-text-subtle)", fontSize: 11 }} tickFormatter={(value) => compactNumber.format(Number(value))} />
              <Tooltip formatter={(value) => currency.format(Number(value))} contentStyle={{ background: "var(--color-panel-strong)", border: "1px solid var(--color-border)", borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Benchmark" fill="var(--color-cyan)" opacity={0.55} radius={[4, 4, 0, 0]} />
              <Bar dataKey="AOI" fill="var(--color-accent)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <div className="business-analysis-grid">
        <section className="panel business-score-table-panel">
          <div className="panel-heading"><div><p>Weighted evaluation model</p><h3>Metric scorecard</h3></div><span className={`business-model-pill${customScenario ? " business-model-pill--custom" : ""}`}>{customScenario ? "Custom scenario" : "BA-2.0 defaults"} · opportunity 75% · fit 25%</span></div>
          <div className="table-scroll">
            <table className="data-table compact business-score-table">
              <thead><tr><th>Metric</th><th>Weight</th><th>AOI</th><th>Comparable</th><th>Score</th></tr></thead>
              <tbody>{selectedResult.scores.map((score) => (
                <tr key={score.key}>
                  <td><strong>{score.label}</strong><small>{score.description}</small></td>
                  <td>{formatScore(score.weight)}%</td><td>{formatMetric(score)}</td><td>{formatMetric(score, true)}</td>
                  <td><span className={`business-score-chip business-score-chip--${scoreTone(score.score)}`}>{score.score}</span></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </section>

        <section className="panel business-ranking-panel">
          <div className="panel-heading"><div><p>Development pipeline</p><h3>AOI ranking</h3></div><span className="business-model-pill">{snapshot.aoiCount} candidates</span></div>
          <div className="business-ranking-list">
            {scenarioSnapshot.aois.map((aoi, index) => (
              <button className={aoi.aoiSk === selectedAoi.aoiSk ? "active" : ""} key={aoi.aoiSk} onClick={() => setSelectedAoiSk(aoi.aoiSk)} type="button">
                <i>{index + 1}</i><div><strong>{aoi.name}</strong><span>{aoi.recommendation}</span></div>
                <b className={`business-score-chip business-score-chip--${scoreTone(aoi.overallScore)}`}>{formatScore(aoi.overallScore)}</b><ArrowUpRight size={15} />
              </button>
            ))}
          </div>
        </section>
      </div>

      <div className="business-method-note">
        <Info size={16} />
        <p><strong>How to read this model.</strong> Each AOI is percentile-ranked against {snapshot.stationCount} operating stations by radius. Opportunity currently combines {formatScore(activeCommercialTotal)}% Esri category expenditure potential with {formatScore(activeDemographicTotal)}% demographic market context; comparable fit gives age characteristics one-quarter of the weight of other profile inputs. The overall score weights the 5/10/20-minute results at 25%/35%/40%. {customScenario ? "A custom weight scenario is active for sensitivity analysis; it does not replace the approved BA-2.0 methodology. " : ""}It is a screening tool—not a final investment decision—and should be followed by traffic counts, competitive mapping, parcel access, environmental review, and project economics. Model {snapshot.methodologyVersion}.</p>
      </div>
    </section>
  );
}
